import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiWorkspaceUpload } from "@/lib/api";

const MAX_BYTES = 50 * 1024 * 1024;

export default function DropZone({ moduleLetter, onUploaded }) {
    const inputRef = useRef(null);
    const [over, setOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pct, setPct] = useState(0);

    useEffect(() => {
        const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
        document.addEventListener("dragover", stop);
        document.addEventListener("drop", stop);
        return () => {
            document.removeEventListener("dragover", stop);
            document.removeEventListener("drop", stop);
        };
    }, []);

    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        setBusy(true);
        setPct(0);
        try {
            for (const f of Array.from(files)) {
                if (f.size > MAX_BYTES) {
                    toast.error(`${f.name}: too large (max 50 MB)`);
                    continue;
                }
                const meta = await apiWorkspaceUpload(moduleLetter || "A", f, setPct);
                toast.success(`Uploaded ${meta.filename}`);
                onUploaded?.(meta);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            setPct(0);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="workspace-dropzone"
            onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setOver(false); }}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                handleFiles(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            className={`
                relative border-2 border-dashed rounded-lg p-12 sm:p-16 text-center cursor-pointer
                transition-all duration-300
                ${over
                    ? "border-tca-cyan bg-tca-cyan/8 shadow-[inset_0_0_28px_rgba(0,219,255,0.18)]"
                    : "border-tca-cyan/25 hover:border-tca-cyan/55 bg-tca-bg-panel/40"}
            `}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="workspace-file-input"
            />
            {busy ? (
                <div className="flex flex-col items-center gap-3">
                    <Loader2 size={26} className="text-tca-cyan animate-spin" />
                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan">
                        Uploading · {pct}%
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-3">
                    <UploadCloud size={32} strokeWidth={1.4} className="text-tca-cyan" />
                    <div className="font-display text-xl text-tca-ink">
                        Drop files here or click to upload
                    </div>
                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">
                        Module {(moduleLetter || "A").toUpperCase()} · max 50 MB per file
                    </div>
                </div>
            )}
        </motion.div>
    );
}
